import { useState, useEffect, useRef } from 'react'
import { fetchData } from '../../../../global/utils/fetch'
import { STATUS } from '../../../../global/types/audit'
import { BEDROCK_MODEL_IDS } from '../../../../global/constants/bedrock-models'
import { getAITrainingStorageKeys } from '../../../../global/constants/local-storage'
import { getAIPrompt, saveAIPrompt } from './api'

interface AIPrompt {
  objectId: string
  auditType: string
  sectionKey: string
  promptText: string
  modelId: string
  isEnabled: boolean
  auditLog?: Array<{
    timestamp: string
    modifiedBy: string
    promptText: string
  }>
}

interface UsePromptManagementOptions {
  auditType: STATUS | null
  sectionKey: string | null
  userEmail?: string
  onSaveSuccess?: () => void
}

export function usePromptManagement({
  auditType,
  sectionKey,
  userEmail,
  onSaveSuccess,
}: UsePromptManagementOptions) {
  const [promptText, setPromptText] = useState('')
  const [isEnabled, setIsEnabled] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [existingPrompt, setExistingPrompt] = useState<AIPrompt | null>(null)
  const [lastSavedToDb, setLastSavedToDb] = useState<Date | null>(null)
  const [lastSavedToLocalStorage, setLastSavedToLocalStorage] =
    useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [selectedModel, setSelectedModel] = useState<string>(
    BEDROCK_MODEL_IDS.CLAUDE_3_5_SONNET_V2,
  )
  const [savedModel, setSavedModel] = useState<string>('')
  const [lastSavedModelToLocalStorage, setLastSavedModelToLocalStorage] =
    useState<Date | null>(null)
  const [isTogglingEnabled, setIsTogglingEnabled] = useState(false)

  const isInitialPromptLoad = useRef(true)
  const isInitialModelLoad = useRef(true)

  const storageKeys = getAITrainingStorageKeys(auditType || '', sectionKey || '')
  const localStorageKey = storageKeys.prompt
  const modelStorageKey = storageKeys.model

  // Load prompt from database and localStorage
  useEffect(() => {
    if (!auditType || !sectionKey) {
      setIsLoading(false)
      return
    }

    const fetchPromptData = async () => {
      setIsLoading(true)

      // Reset state when switching sections
      setPromptText('')
      setExistingPrompt(null)
      setIsEnabled(false)
      setLastSavedToDb(null)
      setLastSavedToLocalStorage(null)
      setLastSavedModelToLocalStorage(null)
      setHasUnsavedChanges(false)

      isInitialPromptLoad.current = true
      isInitialModelLoad.current = true

      try {
        // Load from localStorage first (prompt text)
        const localData = localStorage.getItem(localStorageKey)
        let localPromptText = ''
        let localTimestamp: Date | null = null

        if (localData) {
          try {
            const parsed = JSON.parse(localData)
            localPromptText = parsed.promptText || ''
            localTimestamp = parsed.timestamp
              ? new Date(parsed.timestamp)
              : null
            setLastSavedToLocalStorage(localTimestamp)
          } catch (e) {
            console.error('Failed to parse localStorage data:', e)
          }
        }

        // Load model from localStorage
        const localModelData = localStorage.getItem(modelStorageKey)
        let localModel = ''
        let localModelTimestamp: Date | null = null

        if (localModelData) {
          try {
            const parsed = JSON.parse(localModelData)
            localModel = parsed.modelId || ''
            localModelTimestamp = parsed.timestamp
              ? new Date(parsed.timestamp)
              : null
            setLastSavedModelToLocalStorage(localModelTimestamp)
          } catch (e) {
            // Fallback for old format (plain string)
            localModel = localModelData
            setLastSavedModelToLocalStorage(null)
          }
        }

        // Fetch prompt from database
        const promptData = await fetchData<AIPrompt>(getAIPrompt(auditType, sectionKey))

        // Check if promptData exists and has required fields
        if (promptData && promptData.objectId && promptData.promptText) {
          setExistingPrompt(promptData)
          setIsEnabled(promptData.isEnabled)
          setSavedModel(
            promptData.modelId ||
              BEDROCK_MODEL_IDS.CLAUDE_3_5_SONNET_V2,
          )

          // Use localStorage model if it exists, otherwise use DB model
          if (localModel) {
            setSelectedModel(localModel)
          } else {
            setSelectedModel(
              promptData.modelId ||
                BEDROCK_MODEL_IDS.CLAUDE_3_5_SONNET_V2,
            )
          }

          setLastSavedToDb(
            promptData.auditLog?.[promptData.auditLog.length - 1]?.timestamp
              ? new Date(
                  promptData.auditLog[promptData.auditLog.length - 1].timestamp,
                )
              : null,
          )

          // Use localStorage version if it exists and is different from DB
          if (localPromptText && localPromptText !== promptData.promptText) {
            setPromptText(localPromptText)
            setHasUnsavedChanges(true)
          } else {
            setPromptText(promptData.promptText)
            setHasUnsavedChanges(false)
          }
        } else {
          setExistingPrompt(null)
          setIsEnabled(false)
          setLastSavedToDb(null)

          // Use localStorage model if available, otherwise use default
          if (localModel) {
            setSelectedModel(localModel)
            setSavedModel(BEDROCK_MODEL_IDS.CLAUDE_3_5_SONNET_V2)
          } else {
            setSelectedModel(BEDROCK_MODEL_IDS.CLAUDE_3_5_SONNET_V2)
            setSavedModel(BEDROCK_MODEL_IDS.CLAUDE_3_5_SONNET_V2)
          }

          // Use localStorage version if it exists
          if (localPromptText) {
            setPromptText(localPromptText)
            setHasUnsavedChanges(true)
          } else {
            setPromptText('')
            setHasUnsavedChanges(false)
          }
        }
      } catch (error) {
        console.error('Failed to fetch prompt data:', error)
        setExistingPrompt(null)
        setPromptText('')
        setIsEnabled(false)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPromptData()
  }, [auditType, sectionKey, localStorageKey, modelStorageKey])

  // Auto-save prompt to localStorage with debouncing
  useEffect(() => {
    // Skip saving on initial load
    if (isInitialPromptLoad.current) {
      isInitialPromptLoad.current = false
      return
    }

    const timeoutId = setTimeout(() => {
      if (promptText) {
        const dataToSave = {
          promptText,
          timestamp: new Date().toISOString(),
        }
        localStorage.setItem(localStorageKey, JSON.stringify(dataToSave))
        setLastSavedToLocalStorage(new Date())

        // Check if different from DB version
        if (existingPrompt && promptText !== existingPrompt.promptText) {
          setHasUnsavedChanges(true)
        } else if (!existingPrompt && promptText) {
          setHasUnsavedChanges(true)
        } else {
          setHasUnsavedChanges(false)
        }
      }
    }, 1000) // 1 second debounce

    return () => clearTimeout(timeoutId)
  }, [promptText, existingPrompt, localStorageKey])

  // Save model to localStorage whenever it changes (debounced)
  useEffect(() => {
    // Skip saving on initial load
    if (isInitialModelLoad.current) {
      isInitialModelLoad.current = false
      return
    }

    if (auditType && sectionKey) {
      const timeoutId = setTimeout(() => {
        const dataToSave = {
          modelId: selectedModel,
          timestamp: new Date().toISOString(),
        }
        localStorage.setItem(modelStorageKey, JSON.stringify(dataToSave))
        setLastSavedModelToLocalStorage(new Date())
      }, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [selectedModel, auditType, sectionKey, modelStorageKey])

  // Save prompt to database
  const handleSave = async () => {
    if (!userEmail) {
      console.error('No user email available')
      return { success: false, error: 'No user email available' }
    }

    setIsSaving(true)
    try {
      const data = await fetchData<AIPrompt>(saveAIPrompt({
        auditType,
        sectionKey,
        promptText,
        isEnabled,
        modifiedBy: userEmail,
        modelId: selectedModel,
      }))

      setExistingPrompt(data)
      setSavedModel(selectedModel)
      setLastSavedToDb(new Date())
      setHasUnsavedChanges(false)

      if (onSaveSuccess) {
        onSaveSuccess()
      }

      // Build success message
      const changes: string[] = []
      if (existingPrompt?.promptText !== promptText) changes.push('prompt')
      if (savedModel !== selectedModel) changes.push('model')
      const message =
        changes.length > 0
          ? `Saved ${changes.join(' and ')} to database`
          : 'Saved to database'

      return { success: true, message }
    } catch (error) {
      console.error('Failed to save prompt:', error)
      return { success: false, error: 'Failed to save to database' }
    } finally {
      setIsSaving(false)
    }
  }

  // Revert to saved version from database
  const handleRevertToSaved = () => {
    if (existingPrompt) {
      setPromptText(existingPrompt.promptText)
      setIsEnabled(existingPrompt.isEnabled)
      setSelectedModel(savedModel)
      setHasUnsavedChanges(false)

      // Also update localStorage to match DB
      const dataToSave = {
        promptText: existingPrompt.promptText,
        timestamp: new Date().toISOString(),
      }
      localStorage.setItem(localStorageKey, JSON.stringify(dataToSave))
      setLastSavedToLocalStorage(new Date())
    }
  }

  // Toggle AI auditing enabled/disabled
  const handleToggleEnabled = async (newValue: boolean) => {
    if (!userEmail) {
      return {
        success: false,
        error: 'Cannot toggle: No user email available',
      }
    }

    setIsTogglingEnabled(true)
    try {
      await fetchData<AIPrompt>(saveAIPrompt({
        auditType,
        sectionKey,
        promptText: promptText || existingPrompt?.promptText || '',
        isEnabled: newValue,
        modifiedBy: userEmail,
      }))

      setIsEnabled(newValue)

      if (onSaveSuccess) {
        onSaveSuccess()
      }

      return { success: true }
    } catch (error) {
      console.error('Failed to toggle enabled:', error)
      return { success: false, error: 'Failed to update enabled status' }
    } finally {
      setIsTogglingEnabled(false)
    }
  }

  // Helper to get smart save button text
  const getSaveButtonText = (): string => {
    const promptChanged = hasUnsavedChanges
    const modelChanged = savedModel !== selectedModel

    if (promptChanged && modelChanged) {
      return 'Save Prompt & Model'
    } else if (promptChanged) {
      return 'Save Prompt'
    } else if (modelChanged) {
      return 'Save Model'
    } else {
      return 'Save to Database'
    }
  }

  return {
    // State
    promptText,
    setPromptText,
    isEnabled,
    isLoading,
    isSaving,
    existingPrompt,
    lastSavedToDb,
    lastSavedToLocalStorage,
    hasUnsavedChanges,
    selectedModel,
    setSelectedModel,
    savedModel,
    lastSavedModelToLocalStorage,
    isTogglingEnabled,

    // Actions
    handleSave,
    handleRevertToSaved,
    handleToggleEnabled,
    getSaveButtonText,
  }
}
